/**
 * Country search helpers — prefix / word-start matching and highlight ranges.
 */

export interface CountrySearchMatchRange {
  start: number
  end: number
}

function normalizeQuery(query: string): string {
  return query.trim().toLowerCase()
}

/** True when the display name (or ISO) matches a typed prefix at name or word start. */
export function countryMatchesSearchQuery(
  name: string,
  query: string,
  options?: { countryIso?: string; region?: string }
): boolean {
  const q = normalizeQuery(query)
  if (!q) return true

  const lowerName = name.toLowerCase()
  if (lowerName.startsWith(q)) return true

  const words = lowerName.split(/[\s,./()-]+/).filter(Boolean)
  if (words.some(word => word.startsWith(q))) return true

  const iso = options?.countryIso?.toLowerCase() ?? ''
  if (iso.startsWith(q)) return true

  const region = options?.region?.toLowerCase() ?? ''
  if (region.startsWith(q)) return true

  return false
}

/** Index range in `name` to highlight for the active search query. */
export function findCountrySearchMatchRange(name: string, query: string): CountrySearchMatchRange | null {
  const q = normalizeQuery(query)
  if (!q) return null

  const lowerName = name.toLowerCase()
  if (lowerName.startsWith(q)) {
    return { start: 0, end: q.length }
  }

  const wordPattern = new RegExp(`(^|[\\s,./()-])(${escapeRegExp(q)})`, 'i')
  const match = wordPattern.exec(name)
  if (match && match.index !== undefined && match[2]) {
    const start = match.index + match[1].length
    return { start, end: start + match[2].length }
  }

  return null
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
